import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_REFERRER_KIND } from '@clickersphere/core/enums/ReferrerKindEnum';
import ReferrerService, {
  ICreateReferrerParams,
} from '@clickersphere/core/services/ReferrerService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { REFERRERS_QUERY_KEY, ReferrerQueryData } from './useReferrers';

export function useCreateReferrer() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: createReferrerIsPending,
    mutateAsync: createReferrerAsync,
  } = useMutation({
    mutationFn: async (data: ICreateReferrerParams) => {
      const referrer = await ReferrerService.create(data);
      return referrer as ReferrerQueryData;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });

      const tempId = String(crypto.randomUUID());

      queryClient.setQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
        (old) => [
          ...(old ?? []),
          {
            id: tempId,
            name: variables.name,
            kind: variables.kind ?? DEFAULT_REFERRER_KIND,
            phone: variables.phone ?? null,
            instagram: variables.instagram ?? null,
            leadCount: 0,
            queryStatus: 'pending',
          },
        ],
      );

      return { tempId };
    },

    onSuccess: async (data, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });

      queryClient.setQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
        (old) => {
          const withoutTemp = (old ?? []).filter(
            (referrer) => referrer.id !== context.tempId,
          );
          const alreadyThere = withoutTemp.some(
            (referrer) => referrer.id === data.id,
          );

          return alreadyThere ? withoutTemp : [...withoutTemp, data];
        },
      );
    },

    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(REFERRERS_QUERY_KEY),
      });

      queryClient.setQueryData<ReferrerQueryData[]>(
        buildQueryKey(REFERRERS_QUERY_KEY),
        (old) => old?.filter((referrer) => referrer.id !== context?.tempId),
      );
    },
  });

  return {
    createReferrerAsync,
    createReferrerIsPending,
  };
}
